import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Shared modal accessibility behavior: Escape closes, Tab/Shift+Tab cycles
// only within the modal instead of escaping into the page behind it, focus
// moves into the modal on open, and returns to whatever triggered it on
// close. None of the app's modals had any of this before — a keyboard or
// screen-reader user could tab straight through a "modal" into the blurred
// page underneath it, and Escape did nothing anywhere.
export function useModalA11y(isOpen, onClose, containerRef) {
  const triggerElementRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    triggerElementRef.current = document.activeElement;

    // Wait a tick for the modal's content to actually be in the DOM
    // (several modals mount asynchronously after an entry animation starts).
    const focusTimer = setTimeout(() => {
      const first = containerRef.current?.querySelector(FOCUSABLE_SELECTOR);
      first?.focus();
    }, 50);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !containerRef.current) return;

      const focusable = Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    // Snapshot now, not read again inside cleanup — by the time cleanup
    // runs (often after the container has already unmounted), .current may
    // no longer point at the node we actually attached listeners against.
    const container = containerRef.current;
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      // Only restore focus if it's still where we left it — if the user
      // already clicked elsewhere on the page, don't yank it back.
      if (document.activeElement === document.body || container?.contains(document.activeElement)) {
        triggerElementRef.current?.focus?.();
      }
    };
  }, [isOpen, onClose, containerRef]);
}
